import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, ChevronLeft, Check, Download, Building2, Megaphone, 
  Shield, Sparkles, Clock, AlertCircle, CheckCircle2, Circle,
  Lock, ArrowRight, Loader2, Save, Award, FileText, Printer
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { api } from '../lib/auth';
import { toast } from 'sonner';
import { FloatingParticles, GridPattern, GradientOrb } from '../components/BackgroundEffects';

// Brand tone options
const BRAND_TONES = [
  'Professional', 'Friendly', 'Bold', 'Playful', 'Luxurious', 
  'Trustworthy', 'Innovative', 'Compassionate', 'Authoritative', 'Casual'
];

// Industry verticals
const VERTICALS = [
  'Real Estate', 'Healthcare', 'Legal', 'Finance', 'E-commerce', 
  'Technology', 'Education', 'Fitness', 'Home Services', 'Automotive',
  'Restaurant', 'Beauty & Wellness', 'Construction', 'Insurance', 'Other'
];

// Step configuration
const STEPS = [
  { id: 'identity', title: 'Core Identity', icon: Building2, description: 'Tell us about your business' },
  { id: 'voice', title: 'Brand Voice', icon: Megaphone, description: 'Define your brand personality' },
  { id: 'compliance', title: 'Compliance', icon: Shield, description: 'Legal & regulatory information' },
  { id: 'ai_certification', title: 'AI Certification', icon: Award, description: 'Get AI Certified for your business' },
];

// Debounce hook for auto-save
function useDebounce(callback, delay) {
  const timeoutRef = useRef(null);
  
  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
  
  return debouncedCallback;
}

export default function OnboardingPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [profile, setProfile] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [progress, setProgress] = useState({ overall_percentage: 0, brand_complete: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const pdfContentRef = useRef(null);

  // Load data on mount
  useEffect(() => {
    loadOnboardingData();
  }, []);

  const loadOnboardingData = async () => {
    try {
      const [profileRes, tasksRes, progressRes] = await Promise.all([
        api.getBrandProfile(),
        api.getOnboardingTasks(),
        api.getOnboardingProgress()
      ]);
      setProfile(profileRes.data);
      setTasks(tasksRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Failed to load onboarding data:', error);
      toast.error('Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  };

  // Auto-save function
  const saveProfile = useCallback(async (data) => {
    if (!data) return;
    setSaving(true);
    try {
      const response = await api.updateBrandProfile(data);
      setProfile(response.data);
      // Refresh progress
      const progressRes = await api.getOnboardingProgress();
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  }, []);

  // Debounced save
  const debouncedSave = useDebounce(saveProfile, 1500);

  // Handle field change with auto-save
  const handleFieldChange = (field, value) => {
    const updated = { ...profile, [field]: value };
    setProfile(updated);
    debouncedSave(updated);
  };

  // Handle array field change (for multi-select)
  const handleArrayToggle = (field, value) => {
    const current = profile[field] || [];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    handleFieldChange(field, updated);
  };

  // Handle task status update
  const handleTaskUpdate = async (taskKey, status) => {
    try {
      await api.updateOnboardingTask(taskKey, { status });
      const [tasksRes, progressRes] = await Promise.all([
        api.getOnboardingTasks(),
        api.getOnboardingProgress()
      ]);
      setTasks(tasksRes.data);
      setProgress(progressRes.data);
      toast.success('Task updated');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  // Export PDF
  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const element = pdfContentRef.current;
      
      const opt = {
        margin: [0.5, 0.5],
        filename: `${profile?.dba_name || profile?.legal_business_name || 'Brand'}_Onboarding_Summary.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };
      
      await html2pdf().set(opt).from(element).save();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  // Check if brand breakdown is complete
  const isBrandComplete = profile?.identity_complete && profile?.voice_complete && profile?.compliance_complete;

  // Group tasks by milestone
  const tasksByMilestone = tasks.reduce((acc, task) => {
    if (!acc[task.milestone]) acc[task.milestone] = [];
    acc[task.milestone].push(task);
    return acc;
  }, {});

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="relative min-h-screen">
        {/* Background Effects */}
        <div className="fixed inset-0 pointer-events-none">
          <GridPattern className="opacity-30" />
          <FloatingParticles count={10} />
          <GradientOrb size="lg" position="top-right" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 py-8">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-2">
                  Client Onboarding
                </h1>
                <p className="text-gray-400">Complete your setup to unlock all services</p>
              </div>
              <Button 
                onClick={handleExportPdf}
                disabled={exportingPdf}
                className="btn-secondary flex items-center gap-2"
                data-testid="export-pdf-btn"
              >
                {exportingPdf ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Export PDF
              </Button>
            </div>

            {/* Overall Progress */}
            <div className="mt-6 glass rounded-2xl p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-400">Overall Progress</span>
                <span className="text-sm font-medium text-white">{progress.overall_percentage || 0}%</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <motion.div 
                  className="h-full bg-gradient-to-r from-primary to-cyan-400"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress.overall_percentage || 0}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content - Brand Breakdown */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-2"
            >
              <div className="glass rounded-2xl overflow-hidden">
                {/* Step Header */}
                <div className="p-6 border-b border-white/10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">Step 1</span>
                      <span className="text-gray-600">•</span>
                      <span className="text-xs text-primary font-medium">Brand Breakdown</span>
                    </div>
                    {saving && (
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <Save className="w-3 h-3 animate-pulse" />
                        Saving...
                      </div>
                    )}
                  </div>
                  
                  {/* Step Navigation */}
                  <div className="flex gap-2 flex-wrap">
                    {STEPS.map((step, idx) => {
                      const Icon = step.icon;
                      const isComplete = idx === 0 ? profile?.identity_complete :
                                        idx === 1 ? profile?.voice_complete :
                                        idx === 2 ? profile?.compliance_complete :
                                        profile?.ai_certification_acknowledged;
                      return (
                        <button
                          key={step.id}
                          onClick={() => setCurrentStep(idx)}
                          className={`flex-1 min-w-[120px] p-3 rounded-xl transition-all ${
                            currentStep === idx 
                              ? 'bg-primary/20 border border-primary/50' 
                              : 'bg-white/5 border border-transparent hover:bg-white/10'
                          }`}
                          data-testid={`step-${step.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              isComplete ? 'bg-green-500/20' : 
                              currentStep === idx ? 'bg-primary/20' : 'bg-white/10'
                            }`}>
                              {isComplete ? (
                                <Check className="w-4 h-4 text-green-400" />
                              ) : (
                                <Icon className={`w-4 h-4 ${currentStep === idx ? 'text-primary' : 'text-gray-400'}`} />
                              )}
                            </div>
                            <div className="text-left hidden sm:block">
                              <p className={`text-xs font-medium ${currentStep === idx ? 'text-white' : 'text-gray-400'}`}>
                                {step.title}
                              </p>
                              <p className="text-xs text-gray-500">{step.description}</p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Step Content */}
                <div className="p-6">
                  <AnimatePresence mode="wait">
                    {currentStep === 0 && (
                      <StepIdentity 
                        key="identity"
                        profile={profile} 
                        onChange={handleFieldChange}
                        onArrayToggle={handleArrayToggle}
                      />
                    )}
                    {currentStep === 1 && (
                      <StepVoice 
                        key="voice"
                        profile={profile} 
                        onChange={handleFieldChange}
                        onArrayToggle={handleArrayToggle}
                      />
                    )}
                    {currentStep === 2 && (
                      <StepCompliance 
                        key="compliance"
                        profile={profile} 
                        onChange={handleFieldChange}
                      />
                    )}
                    {currentStep === 3 && (
                      <StepAICertification 
                        key="ai_certification"
                        profile={profile} 
                        onChange={handleFieldChange}
                      />
                    )}
                  </AnimatePresence>

                  {/* Navigation Buttons */}
                  <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
                    <Button
                      variant="ghost"
                      onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                      disabled={currentStep === 0}
                      className="text-gray-400 hover:text-white"
                      data-testid="prev-step-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      Previous
                    </Button>
                    <Button
                      onClick={() => setCurrentStep(Math.min(3, currentStep + 1))}
                      disabled={currentStep === 3}
                      className="btn-primary"
                      data-testid="next-step-btn"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Sidebar - Timeline */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Clock className="w-5 h-5 text-primary" />
                  <h2 className="font-heading text-lg font-semibold text-white">Onboarding Timeline</h2>
                </div>

                {!isBrandComplete && (
                  <div className="mb-6 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-yellow-200 font-medium">Complete Brand Breakdown First</p>
                        <p className="text-xs text-yellow-200/60 mt-1">Some tasks require your brand profile to be complete.</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  {Object.entries(tasksByMilestone).map(([milestone, milestoneTasks]) => (
                    <div key={milestone}>
                      <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-3">{milestone}</h3>
                      <div className="space-y-2">
                        {milestoneTasks.map((task) => {
                          const isBlocked = task.depends_on_brand && !isBrandComplete;
                          const isCompleted = task.status === 'completed';
                          
                          return (
                            <button
                              key={task.task_key}
                              onClick={() => !isBlocked && handleTaskUpdate(task.task_key, isCompleted ? 'pending' : 'completed')}
                              disabled={isBlocked}
                              className={`w-full text-left p-3 rounded-xl transition-all ${
                                isBlocked 
                                  ? 'bg-white/5 opacity-50 cursor-not-allowed' 
                                  : isCompleted
                                    ? 'bg-green-500/10 border border-green-500/20'
                                    : 'bg-white/5 hover:bg-white/10'
                              }`}
                              data-testid={`task-${task.task_key}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                  isBlocked ? 'bg-gray-600' :
                                  isCompleted ? 'bg-green-500' : 'border-2 border-gray-500'
                                }`}>
                                  {isBlocked ? (
                                    <Lock className="w-3 h-3 text-gray-400" />
                                  ) : isCompleted ? (
                                    <Check className="w-3 h-3 text-white" />
                                  ) : null}
                                </div>
                                <div>
                                  <p className={`text-sm font-medium ${isCompleted ? 'text-green-400' : 'text-white'}`}>
                                    {task.title}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-0.5">{task.description}</p>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Hidden PDF Content */}
        <div className="fixed -left-[9999px] -top-[9999px]">
          <div ref={pdfContentRef} className="bg-white text-black p-8 w-[8.5in]">
            <PdfContent profile={profile} tasks={tasks} progress={progress} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

// Step 1: Core Identity
function StepIdentity({ profile, onChange, onArrayToggle }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-2">Legal Business Name *</label>
          <Input
            value={profile?.legal_business_name || ''}
            onChange={(e) => onChange('legal_business_name', e.target.value)}
            placeholder="ACME Corporation LLC"
            className="input-dark"
            data-testid="input-legal-name"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-2">DBA / Brand Name</label>
          <Input
            value={profile?.dba_name || ''}
            onChange={(e) => onChange('dba_name', e.target.value)}
            placeholder="ACME Marketing"
            className="input-dark"
            data-testid="input-dba"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-2">Website Domain *</label>
          <Input
            value={profile?.website_domain || ''}
            onChange={(e) => onChange('website_domain', e.target.value)}
            placeholder="www.example.com"
            className="input-dark"
            data-testid="input-website"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-2">Business Phone *</label>
          <Input
            value={profile?.business_phone || ''}
            onChange={(e) => onChange('business_phone', e.target.value)}
            placeholder="(555) 123-4567"
            className="input-dark"
            data-testid="input-phone"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-2">Business Email *</label>
          <Input
            type="email"
            value={profile?.business_email || ''}
            onChange={(e) => onChange('business_email', e.target.value)}
            placeholder="contact@example.com"
            className="input-dark"
            data-testid="input-email"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-2">License/Registration Numbers</label>
          <Input
            value={profile?.license_numbers || ''}
            onChange={(e) => onChange('license_numbers', e.target.value)}
            placeholder="BRE# 12345678"
            className="input-dark"
            data-testid="input-license"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Physical Address *</label>
        <Textarea
          value={profile?.physical_address || ''}
          onChange={(e) => onChange('physical_address', e.target.value)}
          placeholder="123 Business Ave, Suite 100&#10;City, State 12345"
          className="input-dark min-h-[80px]"
          data-testid="input-address"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Service Areas</label>
        <Input
          value={profile?.service_areas || ''}
          onChange={(e) => onChange('service_areas', e.target.value)}
          placeholder="Los Angeles, Orange County, San Diego..."
          className="input-dark"
          data-testid="input-service-areas"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Primary Industry *</label>
        <div className="flex flex-wrap gap-2">
          {VERTICALS.map((vertical) => (
            <button
              key={vertical}
              type="button"
              onClick={() => onChange('primary_vertical', vertical)}
              className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                profile?.primary_vertical === vertical
                  ? 'bg-primary text-black font-medium'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
              data-testid={`vertical-${vertical.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {vertical}
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// Step 2: Brand Voice
function StepVoice({ profile, onChange, onArrayToggle }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div>
        <label className="block text-sm text-gray-400 mb-2">Brand Tone (Select all that apply) *</label>
        <div className="flex flex-wrap gap-2">
          {BRAND_TONES.map((tone) => (
            <button
              key={tone}
              type="button"
              onClick={() => onArrayToggle('brand_tone', tone)}
              className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                (profile?.brand_tone || []).includes(tone)
                  ? 'bg-primary text-black font-medium'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
              data-testid={`tone-${tone.toLowerCase()}`}
            >
              {tone}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Value Proposition *</label>
        <p className="text-xs text-gray-500 mb-2">What makes your business unique? (1-2 sentences)</p>
        <Textarea
          value={profile?.value_proposition || ''}
          onChange={(e) => onChange('value_proposition', e.target.value)}
          placeholder="We help homeowners find their dream property with personalized service and 20+ years of local expertise..."
          className="input-dark min-h-[100px]"
          data-testid="input-value-prop"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Core Benefits</label>
        <p className="text-xs text-gray-500 mb-2">Top 3 benefits you offer (comma-separated)</p>
        <Input
          value={(profile?.core_benefits || []).join(', ')}
          onChange={(e) => onChange('core_benefits', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
          placeholder="Free consultations, 24/7 support, Money-back guarantee"
          className="input-dark"
          data-testid="input-benefits"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Customer Pain Points</label>
        <p className="text-xs text-gray-500 mb-2">What problems do your customers face? (comma-separated)</p>
        <Input
          value={(profile?.pain_points || []).join(', ')}
          onChange={(e) => onChange('pain_points', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
          placeholder="High costs, Poor communication, Lack of transparency"
          className="input-dark"
          data-testid="input-pain-points"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Brand Story *</label>
        <p className="text-xs text-gray-500 mb-2">Tell us your origin story (2-3 paragraphs)</p>
        <Textarea
          value={profile?.brand_story || ''}
          onChange={(e) => onChange('brand_story', e.target.value)}
          placeholder="Founded in 2010, our journey began when..."
          className="input-dark min-h-[150px]"
          data-testid="input-brand-story"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Elevator Pitch *</label>
        <p className="text-xs text-gray-500 mb-2">30-second pitch for your business</p>
        <Textarea
          value={profile?.elevator_pitch || ''}
          onChange={(e) => onChange('elevator_pitch', e.target.value)}
          placeholder="We're a full-service marketing agency that helps small businesses..."
          className="input-dark min-h-[100px]"
          data-testid="input-elevator-pitch"
        />
      </div>
    </motion.div>
  );
}

// Step 3: Compliance
function StepCompliance({ profile, onChange }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 mb-6">
        <p className="text-sm text-blue-200">
          <strong>Why we need this:</strong> These policies ensure your marketing materials are compliant with industry regulations and protect both you and your customers.
        </p>
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Privacy Policy URL *</label>
        <Input
          value={profile?.privacy_policy || ''}
          onChange={(e) => onChange('privacy_policy', e.target.value)}
          placeholder="https://yoursite.com/privacy-policy"
          className="input-dark"
          data-testid="input-privacy-policy"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Terms & Conditions URL *</label>
        <Input
          value={profile?.terms_conditions || ''}
          onChange={(e) => onChange('terms_conditions', e.target.value)}
          placeholder="https://yoursite.com/terms"
          className="input-dark"
          data-testid="input-terms"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Refund Policy</label>
        <Textarea
          value={profile?.refund_policy || ''}
          onChange={(e) => onChange('refund_policy', e.target.value)}
          placeholder="Describe your refund/cancellation policy..."
          className="input-dark min-h-[100px]"
          data-testid="input-refund"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Required Disclosures</label>
        <p className="text-xs text-gray-500 mb-2">Any industry-specific disclaimers (e.g., "Licensed by XYZ Board")</p>
        <Textarea
          value={profile?.disclosures || ''}
          onChange={(e) => onChange('disclosures', e.target.value)}
          placeholder="Licensed by the State of California Department of Real Estate..."
          className="input-dark min-h-[80px]"
          data-testid="input-disclosures"
        />
      </div>

      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={profile?.results_not_guaranteed_required || false}
            onChange={(e) => onChange('results_not_guaranteed_required', e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-gray-600 bg-gray-800 text-primary focus:ring-primary"
            data-testid="checkbox-results-disclaimer"
          />
          <div>
            <span className="text-sm text-white font-medium">Results Disclaimer Required</span>
            <p className="text-xs text-gray-500 mt-1">
              Check this if your industry requires a "results not guaranteed" disclaimer (e.g., legal, medical, financial services)
            </p>
          </div>
        </label>

        {profile?.results_not_guaranteed_required && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-4"
          >
            <label className="block text-sm text-gray-400 mb-2">Custom Disclaimer Text</label>
            <Textarea
              value={profile?.results_not_guaranteed_text || ''}
              onChange={(e) => onChange('results_not_guaranteed_text', e.target.value)}
              placeholder="Results may vary. Past performance is not indicative of future results..."
              className="input-dark min-h-[80px]"
              data-testid="input-disclaimer-text"
            />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

// Step 4: AI Certification
function StepAICertification({ profile, onChange }) {
  const [activePolicy, setActivePolicy] = useState('internal');
  const [exportingPolicy, setExportingPolicy] = useState(false);
  
  const companyName = profile?.company_name || '[Company Name]';
  const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  
  // Internal AI Policy
  const internalPolicy = `
INTERNAL AI USE POLICY
${companyName}
Effective Date: ${currentDate}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. PURPOSE & SCOPE

This Internal AI Use Policy establishes guidelines for the responsible use of artificial intelligence tools and systems by ${companyName} employees, contractors, and authorized personnel. This policy ensures compliance with legal requirements, protects company and customer data, and maintains ethical standards in AI usage.

2. APPROVED AI TOOLS & SYSTEMS

${companyName} has authorized the use of AI Horse-Powered Technology & Support systems, including but not limited to:
• AI-powered customer service automation
• Content generation and copywriting tools
• Data analysis and reporting systems
• Marketing automation and personalization
• Voice AI assistants and chatbots

All AI tools must be accessed through authorized company accounts and approved platforms only.

3. DATA HANDLING & PRIVACY

3.1 Prohibited Data Inputs
Never input the following into AI systems without explicit authorization:
• Customer personally identifiable information (PII)
• Financial account numbers or payment details
• Medical or health records
• Social Security numbers or government IDs
• Confidential business strategies or trade secrets
• Attorney-client privileged communications

3.2 Data Protection Requirements
• All AI-generated outputs containing customer data must be reviewed before distribution
• Data used for AI training must be anonymized when possible
• Customer consent must be obtained for AI-personalized communications
• All AI interactions are logged and auditable

4. CONTENT REVIEW & ACCURACY

4.1 Human Oversight
• All AI-generated content must be reviewed by a qualified human before publication
• Marketing claims generated by AI must be verified for accuracy
• Legal, medical, or financial advice from AI must be reviewed by licensed professionals

4.2 Quality Standards
• AI outputs must align with ${companyName}'s brand voice and values
• Factual claims must be verified against authoritative sources
• Misleading or deceptive AI-generated content is strictly prohibited

5. CUSTOMER TRANSPARENCY

• Customers must be informed when interacting with AI systems
• AI-generated recommendations must be clearly labeled
• Human escalation options must always be available

6. SECURITY REQUIREMENTS

• Access to AI systems requires multi-factor authentication
• AI credentials must not be shared between employees
• Suspicious AI system behavior must be reported immediately
• Regular security audits of AI integrations are mandatory

7. COMPLIANCE & ENFORCEMENT

Violations of this policy may result in:
• Mandatory retraining on AI use policies
• Restricted access to AI systems
• Disciplinary action up to and including termination
• Legal action if laws are violated

8. POLICY UPDATES

This policy will be reviewed quarterly and updated as AI technology and regulations evolve. Employees will be notified of material changes.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

By using ${companyName}'s AI systems, you acknowledge that you have read, understood, and agree to comply with this Internal AI Use Policy.

Authorized by: ${companyName} Leadership
AI Certified by: AI Horse-Powered Technology & Support
`;

  // External/Customer AI Policy
  const externalPolicy = `
CUSTOMER AI DISCLOSURE & PRIVACY POLICY
${companyName}
Effective Date: ${currentDate}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

INTRODUCTION

${companyName} ("we," "us," or "our") is committed to transparency about our use of artificial intelligence (AI) technologies. This disclosure explains how we use AI in our services, how your data is protected, and your rights regarding AI-powered interactions.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. OUR AI CERTIFICATION

${companyName} is proud to be an AI Certified business through AI Horse-Powered Technology & Support. This certification means we have:

✓ Implemented responsible AI use policies
✓ Trained our team on ethical AI practices
✓ Established data protection protocols
✓ Committed to customer transparency
✓ Passed compliance verification

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

2. HOW WE USE AI

We may use AI technology to:

• Provide faster customer service responses via chat or voice assistants
• Personalize your experience based on your preferences
• Generate content recommendations and suggestions
• Analyze service usage to improve our offerings
• Automate routine communications and follow-ups
• Enhance security and fraud detection

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

3. YOUR DATA & PRIVACY

Data We Collect
• Information you provide directly (name, email, preferences)
• Interaction data (messages, clicks, service usage)
• Technical data (device type, browser, IP address)

How AI Uses Your Data
• To personalize responses and recommendations
• To improve our AI systems' accuracy
• To analyze trends and service performance

We DO NOT:
• Sell your personal data to third parties
• Use your data to train AI models without consent
• Store sensitive information in unsecured AI systems
• Make automated decisions that significantly affect you without human review

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

4. YOUR RIGHTS

You have the right to:

• Know when you are interacting with AI
• Request human assistance at any time
• Access data collected about you
• Request deletion of your data
• Opt out of AI-personalized communications
• File a complaint about AI-related practices

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

5. AI INTERACTION DISCLOSURE

When you interact with our AI systems, you may notice:

• Response indicators showing "AI Assistant" or similar labels
• Slightly faster response times than human agents
• Consistent, helpful answers to common questions
• Seamless handoff to human representatives when needed

Our AI assistants are designed to be helpful, harmless, and honest. They will never:
• Pretend to be human when directly asked
• Provide medical, legal, or financial advice without disclaimers
• Make promises or commitments we cannot honor

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

6. SECURITY MEASURES

We protect your data with:

• Enterprise-grade encryption (in transit and at rest)
• Regular security audits and penetration testing
• Employee training on data handling
• Access controls and authentication requirements
• Incident response procedures

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

7. CONTACT US

Questions about our AI practices? Contact us:

${companyName}
${profile?.business_address || '[Business Address]'}
${profile?.support_email || '[Support Email]'}
${profile?.support_phone || '[Support Phone]'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

CERTIFICATION BADGE

${companyName} is recognized as a "Voted Best of Best" AI Certified Business, demonstrating our commitment to ethical, transparent, and responsible AI use.

This certification qualifies us for:
• Press releases and media recognition
• Best of Best industry listings
• AI Certified Business directory inclusion
• Trust badges for website display

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Last Updated: ${currentDate}
Certified By: AI Horse-Powered Technology & Support
`;

  const handleExportPolicy = async (policyType) => {
    setExportingPolicy(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      
      const content = policyType === 'internal' ? internalPolicy : externalPolicy;
      const title = policyType === 'internal' ? 'Internal AI Use Policy' : 'Customer AI Disclosure Policy';
      
      const element = document.createElement('div');
      element.innerHTML = `
        <div style="font-family: 'Courier New', monospace; font-size: 11px; line-height: 1.6; padding: 40px; white-space: pre-wrap;">
          ${content}
        </div>
      `;
      
      const opt = {
        margin: [0.5, 0.5, 0.5, 0.5],
        filename: `${companyName.replace(/\s+/g, '_')}_${policyType}_AI_Policy.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };
      
      await html2pdf().set(opt).from(element).save();
      toast.success(`${title} exported successfully!`);
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExportingPolicy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-amber-500/20 to-cyan-500/20 mb-4">
          <Award className="w-8 h-8 text-amber-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">AI Certification Program</h3>
        <p className="text-gray-400 text-sm max-w-lg mx-auto">
          Complete your AI certification to become a &quot;Voted Best of Best&quot; AI Certified Business. 
          This qualifies you for press releases, industry listings, and trusted business recognition.
        </p>
      </div>

      {/* Benefits */}
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center">
          <Sparkles className="w-6 h-6 text-amber-400 mx-auto mb-2" />
          <p className="text-sm text-white font-medium">Press Releases</p>
          <p className="text-xs text-gray-500">Media recognition</p>
        </div>
        <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center">
          <Award className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
          <p className="text-sm text-white font-medium">Best of Best</p>
          <p className="text-xs text-gray-500">Industry listings</p>
        </div>
        <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center">
          <Shield className="w-6 h-6 text-green-400 mx-auto mb-2" />
          <p className="text-sm text-white font-medium">Trust Badge</p>
          <p className="text-xs text-gray-500">Website display</p>
        </div>
      </div>

      {/* Policy Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActivePolicy('internal')}
          className={`flex-1 p-3 rounded-xl text-sm font-medium transition-all ${
            activePolicy === 'internal'
              ? 'bg-primary/20 border border-primary/50 text-white'
              : 'bg-white/5 border border-transparent text-gray-400 hover:bg-white/10'
          }`}
        >
          <FileText className="w-4 h-4 inline mr-2" />
          Internal Policy
        </button>
        <button
          onClick={() => setActivePolicy('external')}
          className={`flex-1 p-3 rounded-xl text-sm font-medium transition-all ${
            activePolicy === 'external'
              ? 'bg-primary/20 border border-primary/50 text-white'
              : 'bg-white/5 border border-transparent text-gray-400 hover:bg-white/10'
          }`}
        >
          <FileText className="w-4 h-4 inline mr-2" />
          Customer Policy
        </button>
      </div>

      {/* Policy Content */}
      <div className="bg-black/30 rounded-xl border border-white/10 p-4 max-h-[400px] overflow-y-auto">
        <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono leading-relaxed">
          {activePolicy === 'internal' ? internalPolicy : externalPolicy}
        </pre>
      </div>

      {/* Export Button */}
      <Button
        onClick={() => handleExportPolicy(activePolicy)}
        disabled={exportingPolicy}
        className="w-full btn-primary flex items-center justify-center gap-2"
      >
        {exportingPolicy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        Export {activePolicy === 'internal' ? 'Internal' : 'Customer'} Policy as PDF
      </Button>

      {/* Acknowledgment */}
      <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={profile?.ai_certification_acknowledged || false}
            onChange={(e) => onChange('ai_certification_acknowledged', e.target.checked)}
            className="mt-1 w-5 h-5 rounded border-amber-500/50 bg-black text-amber-500 focus:ring-amber-500"
            data-testid="checkbox-ai-certification"
          />
          <div>
            <span className="text-sm text-white font-medium">
              I acknowledge and agree to implement these AI policies
            </span>
            <p className="text-xs text-amber-200/60 mt-1">
              By checking this box, you confirm that {companyName} will adhere to responsible AI practices 
              and qualifies for &quot;Voted Best of Best&quot; AI Certified Business recognition.
            </p>
          </div>
        </label>
      </div>

      {profile?.ai_certification_acknowledged && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-xl bg-gradient-to-r from-amber-500/20 to-cyan-500/20 border border-amber-500/30 text-center"
        >
          <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <h4 className="text-lg font-bold text-white mb-2">Congratulations!</h4>
          <p className="text-sm text-gray-300 mb-4">
            {companyName} is now an AI Certified Business!
          </p>
          <p className="text-xs text-gray-400">
            Your certification qualifies you for press releases, Best of Best listings, 
            and the AI Certified trust badge for your website.
          </p>
        </motion.div>
      )}
    </motion.div>
  );
}

// PDF Content Component
function PdfContent({ profile, tasks, progress }) {
  const completedTasks = tasks.filter(t => t.status === 'completed');
  const pendingTasks = tasks.filter(t => t.status !== 'completed');

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px', lineHeight: '1.5' }}>
      {/* Header */}
      <div style={{ borderBottom: '2px solid #0891b2', paddingBottom: '16px', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#0891b2', marginBottom: '4px' }}>
          Onboarding Summary
        </h1>
        <p style={{ color: '#666', fontSize: '14px' }}>
          {profile?.dba_name || profile?.legal_business_name || 'Brand Profile'}
        </p>
        <p style={{ color: '#999', fontSize: '11px' }}>
          Generated on {new Date().toLocaleDateString()}
        </p>
      </div>

      {/* Progress */}
      <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '8px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 'bold' }}>Overall Progress</span>
          <span style={{ color: '#0891b2', fontWeight: 'bold' }}>{progress.overall_percentage || 0}%</span>
        </div>
        <div style={{ background: '#ddd', height: '8px', borderRadius: '4px', marginTop: '8px' }}>
          <div style={{ background: '#0891b2', height: '100%', width: `${progress.overall_percentage || 0}%`, borderRadius: '4px' }} />
        </div>
      </div>

      {/* Brand Profile */}
      <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px', borderBottom: '1px solid #ddd', paddingBottom: '8px' }}>
        Brand Profile
      </h2>

      {/* Core Identity */}
      <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#0891b2', marginBottom: '8px' }}>Core Identity</h3>
      <table style={{ width: '100%', marginBottom: '16px', borderCollapse: 'collapse' }}>
        <tbody>
          <tr><td style={{ padding: '4px 8px', color: '#666', width: '35%' }}>Legal Name:</td><td style={{ padding: '4px 8px' }}>{profile?.legal_business_name || '-'}</td></tr>
          <tr style={{ background: '#f9f9f9' }}><td style={{ padding: '4px 8px', color: '#666' }}>DBA:</td><td style={{ padding: '4px 8px' }}>{profile?.dba_name || '-'}</td></tr>
          <tr><td style={{ padding: '4px 8px', color: '#666' }}>Website:</td><td style={{ padding: '4px 8px' }}>{profile?.website_domain || '-'}</td></tr>
          <tr style={{ background: '#f9f9f9' }}><td style={{ padding: '4px 8px', color: '#666' }}>Phone:</td><td style={{ padding: '4px 8px' }}>{profile?.business_phone || '-'}</td></tr>
          <tr><td style={{ padding: '4px 8px', color: '#666' }}>Email:</td><td style={{ padding: '4px 8px' }}>{profile?.business_email || '-'}</td></tr>
          <tr style={{ background: '#f9f9f9' }}><td style={{ padding: '4px 8px', color: '#666' }}>Address:</td><td style={{ padding: '4px 8px' }}>{profile?.physical_address || '-'}</td></tr>
          <tr><td style={{ padding: '4px 8px', color: '#666' }}>Industry:</td><td style={{ padding: '4px 8px' }}>{profile?.primary_vertical || '-'}</td></tr>
          <tr style={{ background: '#f9f9f9' }}><td style={{ padding: '4px 8px', color: '#666' }}>Service Areas:</td><td style={{ padding: '4px 8px' }}>{profile?.service_areas || '-'}</td></tr>
        </tbody>
      </table>

      {/* Brand Voice */}
      <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#0891b2', marginBottom: '8px' }}>Brand Voice</h3>
      <table style={{ width: '100%', marginBottom: '16px', borderCollapse: 'collapse' }}>
        <tbody>
          <tr><td style={{ padding: '4px 8px', color: '#666', width: '35%', verticalAlign: 'top' }}>Brand Tone:</td><td style={{ padding: '4px 8px' }}>{(profile?.brand_tone || []).join(', ') || '-'}</td></tr>
          <tr style={{ background: '#f9f9f9' }}><td style={{ padding: '4px 8px', color: '#666', verticalAlign: 'top' }}>Value Proposition:</td><td style={{ padding: '4px 8px' }}>{profile?.value_proposition || '-'}</td></tr>
          <tr><td style={{ padding: '4px 8px', color: '#666', verticalAlign: 'top' }}>Core Benefits:</td><td style={{ padding: '4px 8px' }}>{(profile?.core_benefits || []).join(', ') || '-'}</td></tr>
          <tr style={{ background: '#f9f9f9' }}><td style={{ padding: '4px 8px', color: '#666', verticalAlign: 'top' }}>Elevator Pitch:</td><td style={{ padding: '4px 8px' }}>{profile?.elevator_pitch || '-'}</td></tr>
        </tbody>
      </table>

      {/* Timeline */}
      <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginTop: '24px', marginBottom: '16px', borderBottom: '1px solid #ddd', paddingBottom: '8px' }}>
        Onboarding Timeline
      </h2>

      <div style={{ display: 'flex', gap: '16px' }}>
        <div style={{ flex: 1 }}>
          <h4 style={{ fontSize: '12px', fontWeight: 'bold', color: '#22c55e', marginBottom: '8px' }}>✓ Completed ({completedTasks.length})</h4>
          {completedTasks.map(task => (
            <div key={task.task_key} style={{ padding: '6px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ fontSize: '11px' }}>{task.title}</span>
            </div>
          ))}
          {completedTasks.length === 0 && <p style={{ color: '#999', fontSize: '11px' }}>No tasks completed yet</p>}
        </div>
        <div style={{ flex: 1 }}>
          <h4 style={{ fontSize: '12px', fontWeight: 'bold', color: '#f59e0b', marginBottom: '8px' }}>○ Pending ({pendingTasks.length})</h4>
          {pendingTasks.map(task => (
            <div key={task.task_key} style={{ padding: '6px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ fontSize: '11px' }}>{task.title}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{ marginTop: '32px', paddingTop: '16px', borderTop: '1px solid #ddd', color: '#999', fontSize: '10px', textAlign: 'center' }}>
        Generated by AI Horse-Powered POD & Support Center
      </div>
    </div>
  );
}
